import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/HuskerHarvestDaysArticle.css';

import BoothSignupImg from '../images/hhd-2026-booth-signup.jpg';
import FarmerConversationImg from '../images/hhd-2026-farmer-conversation.jpg';
import SpurBannerRowImg from '../images/hhd-2026-spur-banner-row.jpg';
import KairospaceBoothImg from '../images/hhd-2026-kairospace-booth.jpg';


/* Back link target: the Resources page */
const BLOG_INDEX_URL = '/resources';


const facts = [
  ['Event', 'Husker Harvest Days 2026'],
  ['Dates', 'Sept. 15–17, 2026'],
  ['Location', 'Near Grand Island, Neb.'],
  ['Program', 'Startup Sandbox Validation Program'],
];


const HuskerHarvestDaysArticle = ({ backHref = BLOG_INDEX_URL }) => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (

  <article className="hhd-article">

    {/* =====================================================
        HEADER
        ===================================================== */}

    <header className="hhd-header">

      <div className="hhd-wrap">

        <Link className="hhd-back" to={backHref}>
          <i className="fas fa-arrow-left" aria-hidden="true" />
          Back to Resources
        </Link>

        <p className="hhd-tag">
          News · Startup Sandbox
        </p>

        <h1>
          Global AgTech Startups Bring Innovation to Nebraska
          Growers at Husker Harvest Days
        </h1>

        <p className="hhd-dek">
          Three international startups in UNL’s Spur Startup Sandbox
          Validation Program showcased technologies, built commercial
          relationships and engaged directly with farmers.
        </p>

        <p className="hhd-byline">
          <span>By Ankit Chandra</span>
          <time dateTime="2026-09-18">Sept. 18, 2026</time>
        </p>

      </div>

    </header>


    <figure className="hhd-hero">

      <img
        src={BoothSignupImg}
        alt="A visitor leans over a table at a startup booth to fill out a form, with growers walking past on the road behind and corn standing in the field"
      />

      <figcaption>
        A visitor stops to fill out a form at one of the startup
        booths at Husker Harvest Days.
      </figcaption>

    </figure>


    {/* =====================================================
        BODY
        ===================================================== */}

    <div className="hhd-wrap hhd-layout">

      <aside className="hhd-facts" aria-label="Event at a glance">

        <h2>At a glance</h2>

        <dl>
          {facts.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>

        <h3>Startups</h3>

        <ul>
          <li>EF Polymer</li>
          <li>Kairospace Technologies</li>
          <li>BharatRohan</li>
        </ul>

      </aside>


      <div className="hhd-body">

        <p className="hhd-dateline">
          <strong>GRAND ISLAND, Neb.</strong> — Sept. 18, 2026
        </p>

        <p className="hhd-lead">
          Three international agricultural technology startups
          participating in the University of Nebraska-Lincoln’s Spur
          Ventures Startup Sandbox Validation Program brought their
          technologies directly to Nebraska farmers, industry leaders
          and potential commercial partners during the 2026 Husker
          Harvest Days, held Sept. 15–17 near Grand Island.
        </p>

        <p>
          EF Polymer, Kairospace Technologies and BharatRohan
          exhibited and demonstrated their technologies as part of the
          University of Nebraska’s SmartAcre Ag Technology Innovation
          Plots, giving the startups an opportunity to move beyond
          laboratory and research settings to engage directly with the
          agricultural community.
        </p>

        <p>
          The three startups are part of Spur Ventures’ Startup
          Sandbox Validation Program, which connects agtech companies
          with Nebraska’s research infrastructure, growers, technical
          expertise and agricultural ecosystem to help validate
          technologies for real-world applications and accelerate
          commercialization. Husker Harvest Days provided the next
          critical piece of that process: direct access to farmers and
          industry.
        </p>

        <p>
          “For us, the real value of Husker Harvest Days was seeing
          validation and commercialization happen side by side,” said
          Ankit Chandra, Founder and Director of Spur Ventures at the
          University of Nebraska-Lincoln. “Our startups demonstrated
          their technologies, listened directly to farmers and built
          relationships that will continue well beyond the show. In
          some cases, those conversations moved directly toward
          commercial transactions — that is what we want the Startup
          Sandbox to enable: helping promising technologies move from
          ‘Does this work?’ to ‘How do we get this into farmers’
          hands?’”
        </p>


        {/* ---------- From international innovation ---------- */}

        <h2>From international innovation to Nebraska farms</h2>

        <p>
          The three startups represent different approaches to
          agricultural innovation.
        </p>

        <h3>EF Polymer</h3>

        <p>
          EF Polymer, a Japan-headquartered startup, showcased its
          biodegradable super-absorbent polymer technology designed to
          improve water and nutrient retention in the crop root zone.
          During the event, the company engaged directly with Nebraska
          producers and converted several farmer conversations into
          product sales — an early example of how international agtech
          companies can use Nebraska as a pathway into the U.S.
          agricultural market.
        </p>

        <figure className="hhd-figure">

          <img
            src={FarmerConversationImg}
            alt="A producer in a ball cap listens as a startup representative points to a handout beside a table of EF Polymer product bags and brochures"
            loading="lazy"
          />

          <figcaption>
            A producer and startup team members talk at the EF Polymer
            booth, under the red tents along the exhibit row.
          </figcaption>

        </figure>

        <blockquote className="hhd-quote">

          <p>
            “What stood out at Husker Harvest Days was the caliber of
            people we met — growers, distributors, retailers — who
            understood exactly what our polymer technology solves. We
            left with real partnership conversations already in
            motion, not just interest.”
          </p>

          <footer>
            <strong>Kenny Toyonaga</strong>
            Head of U.S. Business, EF Polymer
          </footer>

        </blockquote>


        <h3>Kairospace Technologies</h3>

        <div className="hhd-split">

          <div>

            <p>
              Kairospace Technologies demonstrated its nanobubble-based
              technology, highlighting potential applications related
              to water and oxygen availability, soil health and crop
              production.
            </p>

            <blockquote className="hhd-quote">

              <p>
                “Husker Harvest Days let us put our nanobubble
                technology in front of the farmers and industry leaders
                who will actually decide whether to use it. The Spur
                program gave us the access and support to make those
                conversations happen, and they’ll directly shape how
                we approach the U.S. market.”
              </p>

              <footer>
                <strong>Juan Bravin</strong>
                CEO, Kairospace Technologies
              </footer>

            </blockquote>

          </div>

          <figure className="hhd-figure hhd-figure-tall">

            <img
              src={KairospaceBoothImg}
              alt="The Kairospace Technologies booth under a red tent, with two tall banners on either side of a table of equipment and a cornfield behind"
              loading="lazy"
            />

            <figcaption>
              The Kairospace Technologies booth.
            </figcaption>

          </figure>

        </div>


        <h3>BharatRohan</h3>

        <p>
          BharatRohan showcased its hyperspectral drone imaging and
          analytics technology, which uses aerial data to help
          identify crop stress and support more informed agronomic
          decision-making.
        </p>

        <p>
          Across all three, the format gave founders a chance to
          demonstrate their technologies in a working agricultural
          environment while meeting growers who could speak directly
          to practical farm needs, economics, implementation and
          potential adoption.
        </p>


        {/* ---------- Building partnerships ---------- */}

        <h2>Building partnerships beyond the show</h2>

        <p>
          The three-day event also connected founders with
          agricultural companies, researchers, distributors and other
          organizations working to bring new technologies to market.
        </p>

        <p>
          “What stood out was the quality of the conversations,” said
          Chandra. “They went beyond technology — distribution, field
          validation, farmer adoption, commercialization. Those are
          the relationships that determine whether a technology moves
          from a promising startup to a solution farmers actually
          use.”
        </p>

        <p>
          The program is built around that pathway: startups gain
          access to Nebraska’s agricultural ecosystem while
          researchers and growers evaluate emerging technologies in
          real-world conditions. Spur Ventures describes the model as
          a bridge between innovation, validation and adoption.
        </p>

        <blockquote className="hhd-quote hhd-quote-feature">

          <p>
            “Nebraska can be a launchpad for global agtech. We have
            farmers willing to test new ideas, researchers who
            understand agricultural systems and industry partners who
            understand commercialization. Our role at Spur is to
            connect those pieces and help entrepreneurs move from
            technology development to market adoption.”
          </p>

          <footer>
            <strong>Ankit Chandra</strong>
            Founder and Director, Spur Ventures
          </footer>

        </blockquote>


        {/* ---------- A new model ---------- */}

        <h2>A new model for connecting global startups with U.S. agriculture</h2>

        <p>
          The program extends Spur’s mission by giving international
          entrepreneurs a chance to work within Nebraska’s
          agricultural ecosystem — while giving Nebraska growers and
          companies a chance to engage with technologies from around
          the world.
        </p>

        <figure className="hhd-figure">

          <img
            src={SpurBannerRowImg}
            alt="A row of red tents beside a center pivot irrigation system, with a tall Let’s Spur Innovation banner in the foreground and two people shaking hands at a booth"
            loading="lazy"
          />

          <figcaption>
            Startup booths lined up along the exhibit row, with the
            Spur Ventures banner in the foreground.
          </figcaption>

        </figure>

        <p>
          Husker Harvest Days showed how that model can extend beyond
          research trials. “Our goal is not simply to bring startups
          to Nebraska,” Chandra said. “It is to help them build roots
          here — with farmers, researchers, companies and potential
          customers. The next step for these companies is turning this
          week’s relationships and feedback into field trials,
          partnerships and commercial opportunities.”
        </p>


        {/* ---------- About ---------- */}

        <section className="hhd-about" aria-labelledby="hhd-about-title">

          <h2 id="hhd-about-title">About Spur Ventures</h2>

          <p>
            Spur Ventures at the University of Nebraska–Lincoln is
            Nebraska’s translational agtech innovation and venture
            lab, connecting students, researchers, startups, growers
            and industry partners to accelerate agricultural
            technologies from discovery to field adoption and market.
            Through entrepreneurship education, technology validation,
            startup support, industry collaboration and ecosystem
            building, Spur helps entrepreneurs and innovators navigate
            the path from lab to field to market. The Startup Sandbox
            Validation Program connects global agtech startups with
            Nebraska’s agricultural research infrastructure, growers,
            technical expertise and industry ecosystem to validate
            technologies for real-world applications and accelerate
            commercialization.
          </p>

        </section>

      </div>

    </div>

  </article>

  );

};

export default HuskerHarvestDaysArticle;
